import { useEffect, useMemo, useState } from "react";
import FormModal from "@components/common/HPFormModal";
import HPInput from "@components/common/Input";
import { WhitePlusIcon } from "@/assets/images";
import { Box, Typography } from "@mui/material";
import { useAppDispatch } from "@/store";
import { modalDispatcher } from "@/store/slices/modal-slice";
import HPDropdown from "../common/Dropdown";
import { Client } from "@/models/Client";

interface CreateDashboardModalProps {
  // For creation: (dashboardName, primaryClientId, additionalClientIds)
  // For duplication: (dashboardId, dashboardName, primaryClientId, additionalClientIds)
  action: (
    nameOrId: string,
    primaryClientIdOrName: string,
    additionalClientIdsOrPrimaryId: string[],
    additionalClientIds?: string[],
  ) => void;
  clients: Client[];
  closeAction: () => void;
  cancelAction: () => void;
  title?: string;
  confirmButtonText?: string;
  confirmButtonIcon?: React.ReactNode;
  initialName?: string;
  initialPrimaryClientId?: string;
  initialAdditionalClientIds?: string[];
  dashboardId?: string;
}

const CreateDashboardModal = ({
  clients,
  action,
  title = "New dashboard",
  confirmButtonText = "Create",
  confirmButtonIcon = <WhitePlusIcon className="" />,
  initialName = "",
  initialPrimaryClientId = "",
  initialAdditionalClientIds = [],
  dashboardId,
}: CreateDashboardModalProps) => {
  const [dashboardName, setDashboardName] = useState(initialName);
  const [primaryClient, setPrimaryClient] = useState<string | null>(
    initialPrimaryClientId || null,
  );
  const [primaryClientisMissing, setPrimaryClientisMissing] =
    useState<boolean>(false);
  const [additionalClients, setAdditionalClients] = useState<string[]>(
    initialAdditionalClientIds,
  );
  const [isTouched, setIsTouched] = useState(false);
  const characterLimit = 100;
  const allowedCharactersRegex = /^[a-zA-Z0-9 _\-]*$/;

  const dispatch = useAppDispatch();
  const { closeTopModal } = modalDispatcher(dispatch);

  useEffect(() => {
    if (!primaryClient) {
      setAdditionalClients([]);
    }
  }, [primaryClient]);

  const isDashboardNameValid =
    dashboardName.length >= 3 &&
    dashboardName.length <= characterLimit &&
    allowedCharactersRegex.test(dashboardName);

  const isFormValid = isDashboardNameValid && primaryClient !== null;

  const handleCreate = () => {
    if (!isTouched) {
      setIsTouched(true);
    }

    if (!primaryClient) {
      setPrimaryClientisMissing(true);
      return;
    }

    if (isFormValid) {
      // For duplication, pass dashboardId as first parameter
      if (dashboardId) {
        (action as any)(
          dashboardId,
          dashboardName,
          primaryClient,
          additionalClients,
        );
      } else {
        // For creation, call with original signature
        (action as any)(dashboardName, primaryClient, additionalClients);
      }
    }
  };

  const clientOptions = useMemo(() => {
    const options = clients.map((client) => ({
      value: client.id,
      label: client.name,
    }));

    // Sort to put JPMC first, then alphabetically
    return options.sort((a, b) => {
      // Check if either option is JPMC (case-insensitive)
      const aIsJPMC = a.label.toUpperCase().includes("JPMC");
      const bIsJPMC = b.label.toUpperCase().includes("JPMC");

      if (aIsJPMC && !bIsJPMC) return -1;
      if (!aIsJPMC && bIsJPMC) return 1;

      // Both are JPMC or neither is JPMC, sort alphabetically
      return a.label.localeCompare(b.label);
    });
  }, [clients]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isTouched) {
      setIsTouched(true);
    }
    const newValue = e.target.value.slice(0, characterLimit);
    setDashboardName(newValue);
  };

  const getDashboardNameErrorMessage = () => {
    if (!isTouched) {
      return null;
    }
    if (dashboardName.length < 3) {
      return "At least 3 characters needed";
    }
    if (!allowedCharactersRegex.test(dashboardName)) {
      return "Dashboard name contains invalid characters";
    }
    return null;
  };

  return (
    <FormModal
      title={title}
      confirmButtonText={confirmButtonText}
      confirmButtonIcon={confirmButtonIcon}
      onConfirm={handleCreate}
      onCancel={closeTopModal}
    >
      <div className="flex flex-col gap-2">
        <HPInput
          label="Dashboard name*"
          value={dashboardName}
          onChange={handleNameChange}
          placeholder="Enter dashboard name"
          error={isTouched && !isDashboardNameValid}
        />
        <Box className="flex justify-between items-start">
          {getDashboardNameErrorMessage() && isTouched ? (
            <Typography
              variant="caption"
              sx={{ color: "var(--color-hp-error)" }}
            >
              {getDashboardNameErrorMessage()}
            </Typography>
          ) : (
            <Typography variant="caption" className="text-xs max-w-[380px] ">
              At least 3 characters needed. Letters, numbers, spaces, hyphens,
              underscores
            </Typography>
          )}

          <Typography
            variant="body2"
            className={`text-sm ${getDashboardNameErrorMessage() && "text-hp-error"}`}
          >
            {dashboardName.length}/{characterLimit}
          </Typography>
        </Box>
      </div>
      <div className="flex flex-col gap-2">
        <HPDropdown
          label="Primary client*"
          options={clientOptions}
          value={primaryClient || ""}
          onChange={(value) => setPrimaryClient(value as string)}
          placeholder="Select primary client"
          multiSelect={false}
          error={primaryClientisMissing}
        />
      </div>
      <div className="flex flex-col gap-2">
        <HPDropdown
          label="Additional clients"
          options={clientOptions.filter((c) => c.value !== primaryClient)}
          value={additionalClients}
          onChange={(value) => setAdditionalClients(value as string[])}
          placeholder="Select additional clients"
          multiSelect={true}
          disabled={!primaryClient}
        />
      </div>
    </FormModal>
  );
};

export default CreateDashboardModal;
